// SPDX-FileCopyrightText: Copyright 2023 Dash0 Inc.

import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { ChevronDownIcon } from "lucide-react";
import { ComponentProps, Fragment, ReactNode } from "react";

import Link from "next/link";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { namespacedForwardRef } from "@/lib/utils/react/namespaced-forward-ref";

export const buttonVariants = cva(
  "group/button inline-flex border items-center shrink-0 justify-center rounded-button text-sm font-medium ring-offset-background transition-colors [&_svg]:transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "text-button bg-button hover:text-button-hover active:text-button-active border-button hover:bg-button-hover active:bg-button-active disabled:opacity-50 hover:border-button-hover active:border-button-active [&_svg]:text-button-icon [&:hover>svg]:text-button-icon-hover [&:active>svg]:text-button-icon-active data-[state=open]:bg-button-hover data-[state=open]:text-button-hover data-[state=open]:border-button-active [&[data-state=open]>svg]:text-button-icon-hover",
        destructive:
          "bg-button-destructive text-button-destructive border-button-destructive hover:bg-button-destructive-hover active:bg-button-destructive-active [&_svg]:text-button-destructive-icon [&:hover>svg]:text-button-destructive-icon-hover [&:active>svg]:text-button-destructive-icon-active data-[state=open]:bg-button-destructive-hover data-[state=open]:text-button-destructive-hover [&[data-state=open]>svg]:text-button-destructive-icon-hover",
        outline:
          "border-button-outline text-button-outline hover:border-button-outline-hover active:border-button-outline-active hover:text-button-outline-hover active:text-button-outline-active [&_svg]:text-button-outline-icon [&:hover>svg]:text-button-outline-icon-hover [&:active>svg]:text-button-outline-icon-active data-[state=open]:border-button-outline-hover data-[state=open]:text-button-outline-hover [&[data-state=open]>svg]:text-button-outline-icon-hover",
        cta: "bg-button-cta text-button-cta border-button-cta hover:bg-button-cta-hover active:bg-button-cta-active [&_svg]:text-button-cta-icon [&:hover>svg]:text-button-cta-icon-hover [&:active>svg]:text-button-cta-icon-active data-[state=open]:bg-button-cta-hover data-[state=open]:text-button-cta-hover [&[data-state=open]>svg]:text-button-cta-icon-hover",
        selected:
          "bg-button-selected text-button-selected border-button-selected hover:bg-button-selected-hover active:bg-button-selected-active [&_svg]:text-button-selected-icon [&:hover>svg]:text-button-selected-icon-hover [&:active>svg]:text-button-selected-icon-active data-[state=open]:bg-button-selected-hover data-[state=open]:text-button-selected-hover [&[data-state=open]>svg]:text-button-selected-icon-hover",
        transparent:
          "bg-button-transparent text-button-transparent hover:text-button-transparent-hover active:text-button-transparent-active border-button-transparent hover:border-button-transparent-hover active:border-button-transparent-active hover:bg-button-transparent-hover active:bg-button-transparent-active disabled:opacity-50 [&_svg]:text-button-icon [&:hover>svg]:text-button-icon-hover [&:active>svg]:text-button-icon-active data-[state=open]:bg-button-transparent-hover data-[state=open]:text-button-transparent-hover [&[data-state=open]>svg]:text-button-transparent-icon-hover",
        textLink:
          "bg-transparent border-transparent hover:bg-transparent hover:border-transparent underline pl-0 text-default hover:text-default focus-visible:text-default focus-visible:bg-transparent focus-visible:border-transparent active:bg-transparent active:border-transparent active:text-default shadow-none px-0! no-underline",

        "cta-brand":
          "relative border-none rounded h-10 items-center text-center px-4 leading-none group font-medium bg-[#FD8C66] hover:bg-[#F8494D] transition-colors duration-1000 shrink-0 ",
      },
      size: {
        xxs: "h-5 gap-1.5 px-2 text-xs [&_svg]:h-3 [&_svg]:w-3 font-normal",
        xs: "h-6 gap-1.5 px-2 text-xs [&_svg]:h-3.5 [&_svg]:w-3.5",
        sm: "h-8 gap-1.5 px-3 text-sm [&_svg]:h-4 [&_svg]:w-4",
        md: "h-10 gap-2 px-3 text-sm [&_svg]:h-5 [&_svg]:w-5",
        lg: "h-12 gap-3 px-4 text-md [&_svg]:h-5 [&_svg]:w-5",
        xl: "h-16 gap-3 px-5 text-lg [&_svg]:h-6 [&_svg]:w-6",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  }
);

/**
 * We sometimes have the case that we want to baseline-align buttons to a label. This can work fine when both label and
 * button are just text, but we have a common pattern of placing icons into those buttons. The icons are affecting the
 * baseline aligning logic and thus break it.
 */
export const respectBaselineAlignment =
  "before:w-0 before:overflow-hidden before:content-['&']";

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  "data-testid"?: string;
}

export const Button = namespacedForwardRef<
  HTMLButtonElement,
  ButtonProps,
  {
    Tooltip: typeof ButtonTooltip;
    List: typeof ButtonList;
  }
>(
  (
    {
      className,
      variant,
      size,
      children,
      asChild = false,
      type = "button",
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), size, className)}
        type={!asChild ? type : undefined}
        ref={ref}
        {...props}
      >

       <span>
			<span className="relative z-10"> {children}</span>


{variant === "cta-brand" && (
		<div className="absolute pointer-events-none inset-0 overflow-hidden rounded">
			<div className="animate-random-bubble gradient-bubble absolute" />
		</div>
	)}
			 </span>
      </Comp>
    );
  }
);

Button.displayName = "Button";
Button.Tooltip = ButtonTooltip;

const buttonIconVariants = cva("", {
  variants: {
    position: {
      start: "-ml-1",
      end: "-mr-1",
    },
    disclosure: {
      true: "scale-[0.8]",
    },
  },
});

export interface ButtonIconProps
  extends React.ButtonHTMLAttributes<HTMLElement>,
    VariantProps<typeof buttonIconVariants> {}

const ButtonIcon = namespacedForwardRef<
  HTMLElement,
  ButtonIconProps,
  {
    Tooltip: typeof ButtonTooltip;
  }
>(({ className, disclosure, position = "start", children, ...props }, ref) => {
  const Comp = Slot;

  return (
    <Comp
      className={cn(buttonIconVariants({ position, className, disclosure }))}
      ref={ref}
      {...props}
    >
      {children}
    </Comp>
  );
});

ButtonIcon.displayName = "ButtonIcon";
ButtonIcon.Tooltip = ButtonTooltip;

export const ButtonIconStart = (
  props: Omit<ComponentProps<typeof ButtonIcon>, "position" | "disclosure">
) => <ButtonIcon position="start" {...props} />;
export const ButtonIconEnd = (
  props: Omit<ComponentProps<typeof ButtonIcon>, "position" | "disclosure">
) => <ButtonIcon position="end" {...props} />;
export const ButtonIconDisclosure = (
  props: Omit<
    ComponentProps<typeof ButtonIcon>,
    "children" | "position" | "disclosure"
  >
) => (
  <ButtonIcon position="end" disclosure {...props}>
    <ChevronDownIcon />
  </ButtonIcon>
);

export type ButtonLinkItemProps = {
  href: string;
  external?: boolean;
  target?: "_blank";
  download?: string;
  prefetch?: boolean;
} & Omit<ButtonProps, "type" | "asChild">;

export const ButtonLink = namespacedForwardRef<
  HTMLAnchorElement,
  ButtonLinkItemProps,
  {
    Tooltip: typeof ButtonTooltip;
    List: typeof ButtonList;
  }
>(({ href, children, external, download, prefetch, target, ...props }, ref) => {
  // The Link component is incompatible with data URLs, so we need to use an <a> tag instead.
  const Comp = external || href.startsWith("data:") ? "a" : Link;

  return (
    <Button {...props} asChild>
      <Comp
        ref={ref}
        href={href}
        download={download}
        prefetch={prefetch}
        target={target ?? (external ? "_blank" : undefined)}
        rel={external ? "noopener noreferrer" : undefined}
      >
        {children}
      </Comp>
    </Button>
  );
});
ButtonLink.displayName = "ButtonLink";
ButtonLink.Tooltip = ButtonTooltip;

export function ButtonTooltip({
  children,
  tooltip,
  tooltipParams,
  contentParams,
}: {
  children: React.ReactNode;
  tooltip: React.ReactNode;
  tooltipParams?: ComponentProps<typeof Tooltip>;
  contentParams?: ComponentProps<typeof Tooltip.Content>;
}) {
  return (
    <Tooltip {...tooltipParams}>
      <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
      <Tooltip.Content
        {...contentParams}
        className={cn("max-w-[400px]", contentParams?.className)}
      >
        {tooltip}
      </Tooltip.Content>
    </Tooltip>
  );
}

type ButtonListItem =
  | ({
      label: string;
      iconBefore?: ReactNode;
      iconAfter?: ReactNode;
      /**
       * Whether the button should be visible.
       * @default true
       */
      visible?: boolean;
    } & Omit<ButtonLinkItemProps, "children">)
  | ReactNode;

function isButtonListItemAsReactNode(item: ButtonListItem): item is ReactNode {
  if (item == null) {
    return true;
  }
  const isButton =
    typeof item === "object" &&
    "label" in item &&
    typeof item.label === "string";
  return !isButton;
}

export type ButtonListProps = {
  itemProps?: Partial<Omit<ButtonLinkItemProps, "children">>;
  className?: string;
  /**
   * Whether the buttons should be displayed in a column on small screens.
   */
  responsive?: boolean;
} & (
  | {
      children: React.ReactNode;
      items?: never;
    }
  | {
      items: ButtonListItem[];
      children?: never;
    }
);

/**
 * Creates an responsive list of buttons.
 *
 * @example
 * #### With children
 *
 * ```tsx
 * <Button.List>
 *  <Button>Button 1</Button>
 *  <Button>Button 2</Button>
 * </Button.List>
 * ```
 *
 * #### With items
 *
 * ```tsx
 * <Button.List items={[{ label: 'Button 1', onClick: () => {} }]} />
 * ```
 */
export function ButtonList({
  children,
  items,
  itemProps,
  className,
  responsive,
}: ButtonListProps) {
  return (
    <div
      className={cn(
        "flex gap-1",
        {
          "flex-col md:flex-row": responsive,
          "flex-row": !responsive,
        },
        className
      )}
    >
      {children ??
        items?.map((item, index) => {
          if (isButtonListItemAsReactNode(item)) {
            return <Fragment key={index}>{item}</Fragment>;
          }
          const { iconBefore, iconAfter, ...action } = item;
          if (action.visible === false) {
            return null;
          }

          const props = {
            ...itemProps,
            ...action,
            variant:
              action.variant ??
              itemProps?.variant ??
              (index === 0 ? "cta" : "default"),
            className: cn(action.className, itemProps?.className),
          };

          const content = (
            <>
              {iconBefore}
              {action.label}
              {iconAfter}
            </>
          );

          if ("href" in action && action.href != null) {
            return (
              <ButtonLink key={action.label} {...props}>
                {content}
              </ButtonLink>
            );
          }

          return (
            <Button key={action.label} {...props}>
              {content}
            </Button>
          );
        })}
    </div>
  );
}

Button.List = ButtonList;
ButtonLink.List = ButtonList;

ButtonList.Skeleton = function ButtonListSkeleton({
  items,
}: {
  items: number;
}) {
  return (
    <div className="flex flex-col gap-2 md:flex-row">
      {Array.from({ length: items }).map((_, index) => (
        <Skeleton.Button key={index} />
      ))}
    </div>
  );
};
